import './App.css';
import React, { useState, useEffect } from 'react';
import { database } from './firebase/config';
import PolicyModal from './components/PolicyModal';
import { ref, onValue, push } from 'firebase/database';
import { FileUpload } from './components/FileUpload';
import { PageSelection } from './components/PageSelection';
import { PaymentSection } from './components/PaymentSection';
import { PrinterSelection } from './components/PrinterSelection';
import { 
  Shield, Clock, CheckCircle, Zap, TrendingUp, Users, 
  ArrowRight, Download, Printer, CreditCard, ChevronDown, Menu, X 
} from 'lucide-react';

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPages, setSelectedPages] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [selectedPrinter, setSelectedPrinter] = useState('hp_1');
  const [selectedPrinterName, setSelectedPrinterName] = useState('HP LaserJet Pro M404n');
  const [copies, setCopies] = useState(1);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [paymentStatus, setPaymentStatus] = useState({ show: false, message: '', isError: false });
  const [printError, setPrintError] = useState(null);
  const [refundStatus, setRefundStatus] = useState(null);
  
  // Refund Request State
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundForm, setRefundForm] = useState({ jobId: '', contact: '', reason: '' });
  const [activePolicy, setActivePolicy] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobCompleted, setJobCompleted] = useState(false);
  const [failedJobId, setFailedJobId] = useState(null);
  const [isProcessingPrint, setIsProcessingPrint] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    // Call once on mount
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  console.log('App State:', { selectedFile, totalPages, selectedPages, totalCost });

  const handleFileSelect = (file, pages) => {
    console.log('File selected:', file?.name, 'Pages:', pages);
    setSelectedFile(file);
    setTotalPages(pages);
    setSelectedPages([]);
    setTotalCost(0);
  };

  const handlePagesSelect = (pages, cost, selectedCopies = 1) => {
    console.log('Pages selected:', pages, 'Cost:', cost, 'Copies:', selectedCopies);
    setSelectedPages(pages);
    setTotalCost(cost);
    setCopies(selectedCopies);
  };

  const handlePrinterSelect = (printerId, printerName) => {
    console.log('Printer selected:', printerId, printerName);
    setSelectedPrinter(printerId);
    setSelectedPrinterName(printerName);
  };

  const showPaymentStatus = (message, isError = false) => {
    setPaymentStatus({ show: true, message, isError });
    window.setTimeout(() => {
      setPaymentStatus((prev) => ({ ...prev, show: false }));
    }, 3500);
  };

  useEffect(() => {
    if (!currentJobId) return;

    const jobRef = ref(database, `printJobs/${currentJobId}`);
    const unsubscribe = onValue(jobRef, (snapshot) => {
      const job = snapshot.val();
      if (job) {
        if (job.status === 'failed' || job.status === 'completed_with_error' || job.message === '❌ PAGE NAHI MILA') {
          setIsProcessingPrint(false);
          if (!printError) {
            setPrintError("Printer error detected. Click below to initiate refund.");
            setFailedJobId(currentJobId);
            if (job.refund_status) {
              setRefundStatus(job.refund_status);
            }
          }
        } else if (job.status === 'completed' || job.status === 'success') {
          setIsProcessingPrint(false);
          setJobCompleted(true);
        }
      }
    });
    return () => unsubscribe();
  }, [currentJobId, printError]);

  const initiateManualRefund = () => {
    if (!failedJobId) return;
    setRefundStatus("Processing refund...");
    
    const backendUrl = 'https://printapi.krashakinnovativesolution.com';
    fetch(`${backendUrl}/refund-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firebaseJobId: failedJobId })
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        setRefundStatus("Refund Successful! Money will reflect in your account instantly (or within 2-5 minutes).");
      } else {
        setRefundStatus("Automated refund failed. Please request manual refund.");
        setShowRefundModal(true);
        setRefundForm(prev => ({ ...prev, jobId: failedJobId }));
      }
    })
    .catch(err => {
      setRefundStatus("Refund error. Please request manual refund.");
      setShowRefundModal(true);
      setRefundForm(prev => ({ ...prev, jobId: failedJobId }));
    });
  };

  const handlePayment = (paymentData) => {
    if (paymentData?.uploadError) {
      showPaymentStatus(`Payment successful, but upload failed: ${paymentData.uploadError}`, true);
      return;
    }

    if (paymentData?.razorpay_payment_id) {
      const jobId = paymentData?.uploadResult?.jobId;
      if (jobId) {
        setCurrentJobId(jobId);
        setIsProcessingPrint(true);
      }
      showPaymentStatus(
        jobId
          ? `Payment successful. Job queued: ${jobId}`
          : `Payment successful. Your ${selectedPages?.length || 0} pages will be printed shortly.`,
        false
      );

      setSelectedFile(null);
      setTotalPages(0);
      setSelectedPages([]);
      setTotalCost(0);
      setCopies(1);
    } else {
      showPaymentStatus('Payment successful! Your document will be printed shortly.', false);
    }
  };

  const handleRefundSubmit = (e) => {
    e.preventDefault();
    if (!refundForm.jobId || !refundForm.contact || !refundForm.reason) {
      alert("Please fill all fields");
      return;
    }
    const refundRef = ref(database, 'refund_requests');
    push(refundRef, {
      ...refundForm,
      timestamp: new Date().toISOString(),
      status: 'pending'
    }).then(() => {
      alert("Refund request submitted successfully. We will check the logs and process it soon.");
      setShowRefundModal(false);
      setRefundForm({ jobId: '', contact: '', reason: '' });
    }).catch((err) => {
      alert("Error submitting request: " + err.message);
    });
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f9fafb',
      width: '100%',
      overflowX: 'hidden'
    }}>
      <PolicyModal type={activePolicy} onClose={() => setActivePolicy(null)} />
      
      {isProcessingPrint && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '1rem', color: 'white' }}>
          <div style={{ width: '60px', height: '60px', border: '5px solid rgba(255,255,255,0.3)', borderTop: '5px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1.5rem' }}></div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'center' }}>Processing Print Job...</h2>
          <p style={{ fontSize: '1rem', textAlign: 'center', maxWidth: '400px', opacity: 0.9 }}>
            Please wait while we communicate with the printer hardware. This may take up to 20 seconds. Do not refresh the page.
          </p>
          <style>
            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
          </style>
        </div>
      )}

      {printError && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', position: 'relative' }}>
            <button onClick={() => { setPrintError(null); setRefundStatus(null); setFailedJobId(null); }} style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            <div style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '1.25rem', marginBottom: '1rem', textAlign: 'center' }}>{printError}</div>
            
            {refundStatus && <div style={{ fontSize: '0.875rem', marginBottom: '1rem', textAlign: 'center', color: '#374151' }}>Refund Status: <strong>{refundStatus}</strong></div>}
            
            {!refundStatus && (
              <button onClick={initiateManualRefund} style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: 'none', backgroundColor: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 'bold', transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dc2626'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}>
                Refund Now
              </button>
            )}
          </div>
        </div>
      )}

      {jobCompleted && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '16px', width: '100%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeIn 0.3s ease-out' }}>
            <div style={{ width: '64px', height: '64px', backgroundColor: '#10b981', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <svg style={{ width: '32px', height: '32px', color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>Printing Completed!</h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Your document has been successfully printed. Thank you for using PrintPro Services.</p>
            <button onClick={() => { setJobCompleted(false); setCurrentJobId(null); setSelectedFile(null); setSelectedPages([]); setTotalCost(0); setCopies(1); }} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: 'none', backgroundColor: '#10b981', color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex',
          justifyContent: 'center', alignItems: 'center', padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '2rem', borderRadius: '12px',
            width: '100%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Request Refund</h2>
            <form onSubmit={handleRefundSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Job ID or Transaction ID</label>
                <input 
                  type="text" 
                  value={refundForm.jobId}
                  onChange={(e) => setRefundForm({...refundForm, jobId: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  placeholder="e.g. job_1234567"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>UPI ID or Phone Number</label>
                <input 
                  type="text" 
                  value={refundForm.contact}
                  onChange={(e) => setRefundForm({...refundForm, contact: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  placeholder="Where should we send the refund?"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Reason for Refund</label>
                <textarea 
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({...refundForm, reason: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', minHeight: '80px' }}
                  placeholder="e.g. Printer did not print, page was blank, etc."
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={() => setShowRefundModal(false)} style={{ flex: 1, padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: '0.75rem', borderRadius: '6px', border: 'none', backgroundColor: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: '500' }}>Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Navigation */}
      <header style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 50, 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        width: '100%'
      }}>
        <div style={{
          position: 'relative'
        }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            padding: '0.75rem 1rem', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between'
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
            <div style={{ 
              width: 'clamp(40px, 8vw, 50px)', 
              height: 'clamp(40px, 8vw, 50px)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
            }}>
              <img src="/1.png" alt="PrintPro Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 'clamp(16px, 4vw, 20px)', fontWeight: 'bold', color: '#2563eb', margin: 0 }}>
                PrintPro
              </h1>
              <p style={{ fontSize: 'clamp(10px, 2.5vw, 12px)', color: '#6b7280', fontWeight: '500', margin: 0 }}>Smart Printer</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav style={{ 
            display: isMobile ? 'none' : 'flex', 
            alignItems: 'center', 
            gap: 'clamp(1rem, 3vw, 2rem)'
          }}>
            <a href="#features" style={{ 
              color: '#6b7280', 
              textDecoration: 'none', 
              fontSize: '14px', 
              fontWeight: '500'
            }}>Features</a>
            <a href="#pricing" style={{ 
              color: '#6b7280', 
              textDecoration: 'none', 
              fontSize: '14px', 
              fontWeight: '500'
            }}>Pricing</a>
            <a href="#printers" style={{ 
              color: '#6b7280', 
              textDecoration: 'none', 
              fontSize: '14px', 
              fontWeight: '500'
            }}>Printers</a>
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* 24/7 Active Badge */}
            <div style={{ 
              display: isMobile ? 'none' : 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              backgroundColor: '#f0fdf4', 
              padding: '0.5rem 1rem', 
              borderRadius: '9999px', 
              border: '1px solid #bbf7d0'
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                backgroundColor: '#22c55e', 
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#15803d' }}>24/7 Active</span>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{
                display: isMobile ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                cursor: 'pointer',
                color: '#2563eb',
                padding: '0'
              }}
            >
              {isMobileMenuOpen ? 
                <X style={{ 
                  width: '26px', 
                  height: '26px', 
                  strokeWidth: 2.5,
                  color: '#2563eb'
                }} /> : 
                <Menu style={{ 
                  width: '26px', 
                  height: '26px', 
                  strokeWidth: 2.5,
                  color: '#2563eb'
                }} />
              }
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobile && isMobileMenuOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #e5e7eb',
            padding: '1rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 40,
            width: '100%'
          }}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <a href="#features" 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  color: '#6b7280', 
                  textDecoration: 'none', 
                  fontSize: '16px', 
                  fontWeight: '500',
                  padding: '0.5rem 0'
                }}>Features</a>
              <a href="#pricing" 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  color: '#6b7280', 
                  textDecoration: 'none', 
                  fontSize: '16px', 
                  fontWeight: '500',
                  padding: '0.5rem 0'
                }}>Pricing</a>
              <a href="#printers" 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  color: '#6b7280', 
                  textDecoration: 'none', 
                  fontSize: '16px', 
                  fontWeight: '500',
                  padding: '0.5rem 0'
                }}>Printers</a>
              
              {/* Mobile 24/7 Badge */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                backgroundColor: '#f0fdf4', 
                padding: '0.75rem 1rem', 
                borderRadius: '12px', 
                border: '1px solid #bbf7d0',
                marginTop: '0.5rem'
              }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  backgroundColor: '#22c55e', 
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite'
                }}></div>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#15803d' }}>24/7 Active Support</span>
              </div>
            </nav>
          </div>
        )}
        </div>
      </header>

      {paymentStatus.show && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '72px' : '84px',
            right: '16px',
            zIndex: 60,
            maxWidth: isMobile ? 'calc(100% - 32px)' : '420px',
            backgroundColor: paymentStatus.isError ? '#fef2f2' : '#ecfdf5',
            color: paymentStatus.isError ? '#b91c1c' : '#065f46',
            border: `1px solid ${paymentStatus.isError ? '#fecaca' : '#a7f3d0'}`,
            borderRadius: '10px',
            padding: '0.75rem 1rem',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.12)',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          {paymentStatus.message}
        </div>
      )}

      {/* Hero Section */}
      <section style={{ 
        position: 'relative',
        color: 'white', 
        padding: 'clamp(3rem, 8vw, 6rem) 0',
        overflow: 'hidden',
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center'
      }}>
        {/* Background Video */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%)',
            zIndex: 0
          }}
        >
          <source src="/Video Project 2.mp4" type="video/mp4" />
        </video>

        {/* Dark Overlay for readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          zIndex: 1
        }}></div>

        <div style={{ 
          position: 'relative',
          zIndex: 2,
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 clamp(1rem, 4vw, 1.5rem)',
          width: '100%'
        }}>
          <div style={{ maxWidth: '1024px', textAlign: 'center', margin: '0 auto' }}>
            <div style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: 'clamp(1rem, 3vw, 2rem)', 
              padding: 'clamp(0.5rem, 1vw, 0.75rem) clamp(1rem, 2vw, 1.5rem)', 
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white', 
              borderRadius: '9999px', 
              fontSize: 'clamp(12px, 2.5vw, 14px)', 
              fontWeight: '600',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <span style={{ fontSize: '16px' }}>✨</span> Professional Printing Solutions
            </div>

            <h1 style={{ 
              fontSize: 'clamp(36px, 6vw, 72px)', 
              fontWeight: '800', 
              color: 'white', 
              lineHeight: '1.1', 
              marginBottom: '1.5rem',
              margin: '0 auto 1.5rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              Print with <span style={{ 
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'inline-block'
              }}>Confidence</span>
            </h1>

            <p style={{ 
              fontSize: 'clamp(18px, 3vw, 24px)', 
              color: '#e2e8f0', 
              marginBottom: '3rem', 
              lineHeight: '1.6', 
              maxWidth: '650px', 
              margin: '0 auto 3rem',
              fontWeight: '400',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}>
              Upload, select, pay. Your documents get printed to the right printer in minutes. Zero hassle.
            </p>

            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row', 
              gap: '1.5rem', 
              justifyContent: 'center', 
              alignItems: 'center', 
              marginBottom: '4rem',
              flexWrap: 'wrap',
              width: '100%'
            }}>
              <button 
                onClick={() => {
                  const printingSection = document.getElementById('printing-section');
                  if (printingSection) {
                    printingSection.scrollIntoView({ 
                      behavior: 'smooth', 
                      block: 'start' 
                    });
                  }
                }}
                style={{ 
                  padding: 'clamp(0.875rem, 2vw, 1.25rem) clamp(2rem, 4vw, 2.5rem)', 
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
                  color: 'white', 
                  fontWeight: '700', 
                  borderRadius: '12px', 
                  border: 'none', 
                  cursor: 'pointer',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.5)',
                  fontSize: 'clamp(16px, 3vw, 18px)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 15px 30px -5px rgba(37, 99, 235, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(37, 99, 235, 0.5)';
                }}
              >
                <span>Start Printing Now</span>
                <ArrowRight style={{ width: '22px', height: '22px' }} />
              </button>
              
              <button 
                style={{ 
                  padding: 'clamp(0.875rem, 2vw, 1.25rem) clamp(2rem, 4vw, 2.5rem)', 
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border: '2px solid rgba(255, 255, 255, 0.8)', 
                  color: 'white', 
                  fontWeight: '700', 
                  borderRadius: '12px', 
                  cursor: 'pointer',
                  fontSize: 'clamp(16px, 3vw, 18px)',
                  transition: 'background 0.2s ease, transform 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Watch Demo
              </button>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
              gap: 'clamp(1rem, 2vw, 1.5rem)', 
              maxWidth: '800px', 
              margin: '0 auto'
            }}>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.1)', 
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '16px', 
                padding: '1.5rem 1rem', 
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '800', color: 'white', marginBottom: '4px' }}>5000+</div>
                <div style={{ fontSize: '15px', color: '#e2e8f0', fontWeight: '500' }}>Documents Printed</div>
              </div>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.1)', 
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '16px', 
                padding: '1.5rem 1rem', 
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '800', color: 'white', marginBottom: '4px' }}>99.9%</div>
                <div style={{ fontSize: '15px', color: '#e2e8f0', fontWeight: '500' }}>Uptime</div>
              </div>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.1)', 
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '16px', 
                padding: '1.5rem 1rem', 
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{ fontSize: '32px', fontWeight: '800', color: 'white', marginBottom: '4px' }}>24/7</div>
                <div style={{ fontSize: '15px', color: '#e2e8f0', fontWeight: '500' }}>Active Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section id="features" style={{ padding: '5rem 0', backgroundColor: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ 
              fontSize: 'clamp(32px, 5vw, 48px)', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '1rem',
              margin: '0 auto 1rem'
            }}>Why Businesses Choose PrintPro</h2>
            <p style={{ fontSize: '20px', color: '#6b7280', margin: 0 }}>Everything you need, nothing you don't</p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '2rem'
          }}>
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '2rem', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              transition: 'box-shadow 0.3s ease'
            }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                borderRadius: '12px', 
                backgroundColor: '#2563eb', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginBottom: '1.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                <Shield style={{ width: '28px', height: '28px', color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>Bank-Grade Security</h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', margin: 0 }}>End-to-end encryption for all documents. Your data stays private.</p>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '2rem', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              transition: 'box-shadow 0.3s ease'
            }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                borderRadius: '12px', 
                backgroundColor: '#7c3aed', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginBottom: '1.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                <Zap style={{ width: '28px', height: '28px', color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>Lightning Fast</h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', margin: 0 }}>Average processing time under 2 minutes. No queues, no delays.</p>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '2rem', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              transition: 'box-shadow 0.3s ease'
            }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                borderRadius: '12px', 
                backgroundColor: '#059669', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginBottom: '1.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                <TrendingUp style={{ width: '28px', height: '28px', color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>99.9% Reliability</h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', margin: 0 }}>Enterprise-grade infrastructure with automatic failover.</p>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '2rem', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              transition: 'box-shadow 0.3s ease'
            }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                borderRadius: '12px', 
                backgroundColor: '#ea580c', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginBottom: '1.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                <Users style={{ width: '28px', height: '28px', color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>Multiple Printers</h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', margin: 0 }}>Choose from various printer options for your needs.</p>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '2rem', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              transition: 'box-shadow 0.3s ease'
            }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                borderRadius: '12px', 
                backgroundColor: '#4338ca', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginBottom: '1.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                <CheckCircle style={{ width: '28px', height: '28px', color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>Professional Quality</h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', margin: 0 }}>Premium output from enterprise-grade equipment.</p>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              padding: '2rem', 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              transition: 'box-shadow 0.3s ease'
            }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                borderRadius: '12px', 
                backgroundColor: '#db2777', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                marginBottom: '1.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                <Clock style={{ width: '28px', height: '28px', color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' }}>24/7 Support</h3>
              <p style={{ color: '#6b7280', lineHeight: '1.6', margin: 0 }}>Dedicated support available round the clock.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={{ 
        padding: 'clamp(3rem, 8vw, 5rem) 0', 
        background: 'linear-gradient(135deg, #f9fafb 0%, #e0f2fe 100%)' 
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(1rem, 4vw, 1.5rem)' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ 
              fontSize: 'clamp(32px, 5vw, 48px)', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '1rem',
              margin: '0 auto 1rem'
            }}>Simple & Transparent Pricing</h2>
            <p style={{ 
              fontSize: '20px', 
              color: '#6b7280',
              margin: 0
            }}>No surprises, no hidden fees</p>
          </div>

          <div style={{ maxWidth: '560px', width: '100%', margin: '0 auto' }}>
            <div style={{ 
              background: 'linear-gradient(90deg, #3b82f6 0%, #1e40af 100%)', 
              borderRadius: '16px', 
              padding: 'clamp(1.25rem, 5vw, 3rem)', 
              color: 'white', 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              width: '100%',
              boxSizing: 'border-box' 
            }}>
              <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <p style={{ color: '#bfdbfe', fontSize: 'clamp(16px, 4vw, 18px)', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>Base Rate</p>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'baseline', 
                  justifyContent: 'center', 
                  gap: '0.5rem', 
                  marginBottom: '0.5rem' 
                }}>
                  <span style={{ fontSize: 'clamp(52px, 16vw, 72px)', fontWeight: 'bold', lineHeight: 1 }}>₹2</span>
                  <span style={{ fontSize: 'clamp(28px, 8vw, 32px)', color: '#bfdbfe' }}>/page</span>
                </div>
                <p style={{ color: '#bfdbfe', fontSize: 'clamp(16px, 4vw, 18px)', margin: 0 }}>That's it. No minimum orders.</p>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', 
                gap: 'clamp(0.75rem, 3vw, 1.5rem)', 
                marginBottom: '3rem' 
              }}>
                <div style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '12px', 
                  padding: 'clamp(0.9rem, 3vw, 1.5rem)', 
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'background-color 0.3s ease'
                }}>
                  <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', color: '#bfdbfe', marginBottom: '0.5rem' }}>5 Pages</div>
                  <div style={{ fontSize: 'clamp(30px, 8vw, 32px)', fontWeight: 'bold', lineHeight: 1.1 }}>₹10</div>
                </div>
                <div style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '12px', 
                  padding: 'clamp(0.9rem, 3vw, 1.5rem)', 
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'background-color 0.3s ease'
                }}>
                  <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', color: '#bfdbfe', marginBottom: '0.5rem' }}>10 Pages</div>
                  <div style={{ fontSize: 'clamp(30px, 8vw, 32px)', fontWeight: 'bold', lineHeight: 1.1 }}>₹20</div>
                </div>
                <div style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '12px', 
                  padding: 'clamp(0.9rem, 3vw, 1.5rem)', 
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'background-color 0.3s ease'
                }}>
                  <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', color: '#bfdbfe', marginBottom: '0.5rem' }}>20 Pages</div>
                  <div style={{ fontSize: 'clamp(30px, 8vw, 32px)', fontWeight: 'bold', lineHeight: 1.1 }}>₹40</div>
                </div>
              </div>

              <div style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: '12px', 
                padding: '1.5rem', 
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <p style={{ color: '#bfdbfe', margin: 0 }}>
                  💡 <span style={{ fontWeight: '600' }}>Bulk Discounts</span> available for regular customers
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Printers Section */}
      <section id="printers" style={{ padding: '5rem 0', backgroundColor: '#f9fafb' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ 
              fontSize: 'clamp(32px, 5vw, 48px)', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '1rem',
              margin: '0 auto 1rem'
            }}>Supported Printers</h2>
            <p style={{ fontSize: '20px', color: '#6b7280', margin: 0 }}>Choose from premium printing equipment</p>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '2rem', 
            maxWidth: '1280px', 
            margin: '0 auto' 
          }}>
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              border: '1px solid #e5e7eb', 
              overflow: 'hidden',
              transition: 'box-shadow 0.3s ease',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ height: '4px', backgroundColor: '#ea580c' }}></div>
              <div style={{ padding: '2rem' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '8px', 
                  backgroundColor: '#ea580c', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '1.5rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Printer style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
                  HP LaserJet Pro M404n
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>38 ppm</span>
                  </li>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>Professional Quality</span>
                  </li>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>Monochrome</span>
                  </li>
                </ul>
              </div>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              border: '1px solid #e5e7eb', 
              overflow: 'hidden',
              transition: 'box-shadow 0.3s ease',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ height: '4px', backgroundColor: '#3b82f6' }}></div>
              <div style={{ padding: '2rem' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '8px', 
                  backgroundColor: '#3b82f6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '1.5rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Printer style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
                  Samsung Xpress M2026W
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>26 ppm</span>
                  </li>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>Monochrome</span>
                  </li>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>WiFi Enabled</span>
                  </li>
                </ul>
              </div>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '16px', 
              border: '1px solid #e5e7eb', 
              overflow: 'hidden',
              transition: 'box-shadow 0.3s ease',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ height: '4px', backgroundColor: '#8b5cf6' }}></div>
              <div style={{ padding: '2rem' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '8px', 
                  backgroundColor: '#8b5cf6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '1.5rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Printer style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
                  Samsung Xpress C460W
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>Color + Monochrome</span>
                  </li>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>25 ppm</span>
                  </li>
                  <li style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '0.75rem', 
                    color: '#374151'
                  }}>
                    <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', marginRight: '0.75rem' }} />
                    <span style={{ fontWeight: '500' }}>Professional Output</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section style={{ padding: '5rem 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
            <h2 style={{ 
              fontSize: 'clamp(32px, 5vw, 48px)', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '4rem',
              textAlign: 'center',
              margin: '0 auto 4rem'
            }}>How It Works</h2>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '2rem' 
            }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  backgroundColor: '#3b82f6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '1rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Download style={{ width: '32px', height: '32px', color: 'white' }} />
                </div>
                <h3 style={{ fontWeight: 'bold', color: '#111827', fontSize: '18px', margin: '0 0 0.5rem 0' }}>Upload</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', margin: 0 }}>Select your PDF</p>
              </div>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  backgroundColor: '#059669', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '1rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  <CheckCircle style={{ width: '32px', height: '32px', color: 'white' }} />
                </div>
                <h3 style={{ fontWeight: 'bold', color: '#111827', fontSize: '18px', margin: '0 0 0.5rem 0' }}>Pages</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', margin: 0 }}>Choose pages</p>
              </div>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  backgroundColor: '#8b5cf6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '1rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Printer style={{ width: '32px', height: '32px', color: 'white' }} />
                </div>
                <h3 style={{ fontWeight: 'bold', color: '#111827', fontSize: '18px', margin: '0 0 0.5rem 0' }}>Printer</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', margin: 0 }}>Pick printer</p>
              </div>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  backgroundColor: '#ea580c', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginBottom: '1rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  <CreditCard style={{ width: '32px', height: '32px', color: 'white' }} />
                </div>
                <h3 style={{ fontWeight: 'bold', color: '#111827', fontSize: '18px', margin: '0 0 0.5rem 0' }}>Pay</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', margin: 0 }}>Complete</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Printing Interface Section */}
      <section id="printing-section" style={{ 
        padding: 'clamp(2rem, 6vw, 3rem) 0', 
        backgroundColor: '#f9fafb' 
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 clamp(1rem, 4vw, 1.5rem)' 
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: 'clamp(1rem, 3vw, 1.5rem)',
            alignItems: 'stretch'
          }}>
            {/* Step 1 - Upload */}
            <div style={{ height: '100%' }}>
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '12px', 
                border: '2px solid #60a5fa', 
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{ 
                  backgroundColor: '#dbeafe', 
                  padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1rem, 3vw, 1.5rem)', 
                  borderBottom: '1px solid #e5e7eb',
                  flexShrink: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ 
                      width: '24px', 
                      height: '24px', 
                      borderRadius: '50%', 
                      backgroundColor: '#3b82f6', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      color: 'white', 
                      fontWeight: 'bold', 
                      fontSize: '12px'
                    }}>1</div>
                    <div>
                      <h4 style={{ fontWeight: 'bold', color: '#111827', margin: 0, fontSize: '16px' }}>Upload</h4>
                      <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>PDF, Word, Text files</p>
                    </div>
                  </div>
                </div>
                <div style={{ 
                  padding: '1.5rem', 
                  minHeight: '120px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flex: 1
                }}>
                  <FileUpload onFileSelect={handleFileSelect} />
                </div>
              </div>
            </div>

            {/* Step 2 - Select Pages */}
            {selectedFile && (
              <div style={{ height: '100%' }}>
                <div style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '12px', 
                  border: '2px solid #4ade80', 
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ 
                    backgroundColor: '#dcfce7', 
                    padding: '1rem 1.5rem', 
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%', 
                        backgroundColor: '#059669', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '12px'
                      }}>2</div>
                      <div>
                        <h4 style={{ fontWeight: 'bold', color: '#111827', margin: 0, fontSize: '16px' }}>Pages</h4>
                        <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                          {selectedPages.length} selected • ₹{totalCost}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    padding: '1.5rem', 
                    minHeight: '120px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flex: 1
                  }}>
                    <PageSelection 
                      totalPages={totalPages} 
                      onPagesSelect={handlePagesSelect} 
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 - Select Printer */}
            {selectedPages.length > 0 && totalCost > 0 && (
              <div style={{ height: '100%' }}>
                <div style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '12px', 
                  border: '2px solid #a78bfa', 
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ 
                    backgroundColor: '#ede9fe', 
                    padding: '1rem 1.5rem', 
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%', 
                        backgroundColor: '#8b5cf6', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '12px'
                      }}>3</div>
                      <div>
                        <h4 style={{ fontWeight: 'bold', color: '#111827', margin: 0, fontSize: '16px' }}>Printer</h4>
                        <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedPrinterName || 'Choose printer'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    padding: '1.5rem', 
                    minHeight: '120px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flex: 1
                  }}>
                    <PrinterSelection 
                      selectedPrinter={selectedPrinter}
                      onPrinterSelect={handlePrinterSelect}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4 - Payment */}
            {selectedPages.length > 0 && totalCost > 0 && selectedPrinter && (
              <div style={{ height: '100%' }}>
                <div style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '12px', 
                  border: '2px solid #fb923c', 
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ 
                    backgroundColor: '#fed7aa', 
                    padding: '1rem 1.5rem', 
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%', 
                        backgroundColor: '#ea580c', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '12px'
                      }}>4</div>
                      <div>
                        <h4 style={{ fontWeight: 'bold', color: '#111827', margin: 0, fontSize: '16px' }}>Payment</h4>
                        <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>Secure Checkout</p>
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    padding: '1.5rem', 
                    minHeight: '120px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flex: 1
                  }}>
                    <PaymentSection 
                      selectedPages={selectedPages}
                      totalCost={totalCost}
                      copies={copies}
                      selectedFile={selectedFile}
                      selectedPrinter={selectedPrinter}
                      onPayment={handlePayment}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ 
        backgroundColor: '#111827', 
        color: 'white', 
        paddingTop: 'clamp(3rem, 8vw, 5rem)', 
        paddingBottom: 'clamp(2rem, 5vw, 3rem)' 
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 clamp(1rem, 4vw, 1.5rem)' 
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 'clamp(2rem, 5vw, 3rem)', 
            marginBottom: 'clamp(2rem, 5vw, 4rem)' 
          }}>
            {/* Brand Footer */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  backgroundColor: '#3b82f6', 
                  borderRadius: '12px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Printer style={{ width: '20px', height: '20px', color: 'white' }} />
                </div>
                <h5 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>PrintPro</h5>
              </div>
              <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                Professional document printing solutions designed for businesses that value security and reliability.
              </p>
            </div>

            {/* Product */}
            <div>
              <h6 style={{ fontWeight: 'bold', color: 'white', marginBottom: '1.5rem', margin: '0 0 1.5rem 0' }}>Product</h6>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Features</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Pricing</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Security</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>API</a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h6 style={{ fontWeight: 'bold', color: 'white', marginBottom: '1.5rem', margin: '0 0 1.5rem 0' }}>Company</h6>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>About Us</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Blog</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Careers</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Contact</a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h6 style={{ fontWeight: 'bold', color: 'white', marginBottom: '1.5rem', margin: '0 0 1.5rem 0' }}>Legal</h6>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" onClick={(e) => { e.preventDefault(); setActivePolicy('privacy'); }} style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Privacy Policy</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" onClick={(e) => { e.preventDefault(); setActivePolicy('terms'); }} style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Terms of Service</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" onClick={(e) => { e.preventDefault(); setActivePolicy('refund'); }} style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Refund & Cancellation Policy</a>
                </li>
                <li style={{ marginBottom: '0.75rem' }}>
                  <a href="#" onClick={(e) => { e.preventDefault(); setActivePolicy('contact'); }} style={{ 
                    color: '#9ca3af', 
                    fontSize: '14px', 
                    fontWeight: '500',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Contact Us</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #374151', paddingTop: '2rem', marginBottom: '2rem' }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: '1rem'
            }}>
              <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0, textAlign: 'center' }}>
                © 2026 PrintPro Services. All rights reserved.
              </p>
              <p style={{ 
                color: '#60a5fa', 
                fontSize: '13px', 
                margin: 0, 
                textAlign: 'center',
                fontWeight: '500'
              }}>
                Made by AI & Robotics Lab ECB College Bikaner
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                <a href="mailto:nahatakaran52@gmail.com" style={{ 
                  color: '#9ca3af', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  textDecoration: 'none',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'white'}
                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>
                  <span>📧</span>
                  <span>nahatakaran52@gmail.com</span>
                </a>
                <a href="tel:+916350291328" style={{ 
                  color: '#9ca3af', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  textDecoration: 'none',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = 'white'}
                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>
                  <span>📞</span>
                  <span>+91 6350291328</span>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Badge */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ 
              padding: '0.5rem 1rem', 
              backgroundColor: '#1f2937', 
              border: '1px solid #4b5563', 
              borderRadius: '9999px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem' 
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                backgroundColor: '#10b981', 
                borderRadius: '50%',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }}></div>
              <span style={{ fontSize: '12px', color: '#d1d5db' }}>All systems operational</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
